import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, AlertCircle, Package } from 'lucide-react';
import COUNTRIES from '../utils/countries';
import { ComplianceFieldset, EMPTY_COMPLIANCE } from '../components/ComplianceFieldset';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADVANCED_SUBJECT = 'GPU / AI Compute & Advanced Hardware';

const contactInfo = [
  {
    icon: MapPin,
    title: 'Address',
    content: '40 Bank Street, Level 18 Servcorp',
    subtext: 'Canary Wharf, London, E14 5NR, UK'
  },
  {
    icon: Phone,
    title: 'Phone',
    content: '+44(0) 203 059 4550',
    subtext: 'Mon-Fri 9am-6pm GMT'
  },
  {
    icon: Mail,
    title: 'Email',
    content: 'info@realtechservices.co.uk',
    subtext: 'For enquiries'
  },
  {
    icon: Clock,
    title: 'Business Hours',
    content: 'Monday - Friday',
    subtext: '9:00 AM - 6:00 PM GMT'
  }
];

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    address: '',
    country: '',
    country_code: '',
    postcode: '',
    vat_number: '',
    subject: searchParams.get('enquiry') === 'advanced-compute' ? ADVANCED_SUBJECT : '',
    message: '',
    consent_offers: false
  });
  const [compliance, setCompliance] = useState(EMPTY_COMPLIANCE);
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [taxonomy, setTaxonomy] = useState([]);
  const isAdvanced = formData.subject === ADVANCED_SUBJECT;

  // Category context carried over from the catalogue (preserved into the enquiry record)
  const requestedCategory = searchParams.get('category') || '';
  const requestedType = searchParams.get('type') || '';
  const requestedSub = searchParams.get('sub') || '';
  const requestedProduct = searchParams.get('product') || '';
  const catInfo = taxonomy.find((c) => c.slug === requestedCategory);
  const typeInfo = catInfo && requestedType ? (catInfo.families || []).find((f) => f.slug === requestedType) : null;
  const requestedLabel = catInfo
    ? `${catInfo.name}${typeInfo ? ' › ' + typeInfo.name : requestedSub ? ' › ' + requestedSub : ''}${requestedProduct ? ' — ' + requestedProduct : ''}`
    : requestedProduct;

  useEffect(() => {
    if (!requestedCategory) return;
    axios.get(`${BACKEND_URL}/api/catalogue/taxonomy`).then((r) => setTaxonomy(r.data)).catch(() => {});
  }, [requestedCategory]);

  useEffect(() => {
    const advanced = searchParams.get('enquiry') === 'advanced-compute' || !!(catInfo && catInfo.export_controlled);
    const ready = !requestedCategory || !!catInfo;
    setFormData(prev => {
      const next = { ...prev };
      if (advanced) next.subject = ADVANCED_SUBJECT;
      else if (!prev.subject && requestedCategory) next.subject = 'Wholesale Inquiry';
      if (ready && !prev.message && requestedLabel) {
        next.message = `Requesting current availability for: ${requestedLabel}.` +
          (searchParams.get('requirements') ? '\n\nQuantity required:\nDelivery location:\nRequired lead time:\nSpecification / configuration notes:\n' : '');
      }
      return next;
    });
  }, [searchParams, catInfo, requestedCategory, requestedLabel]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleComplianceChange = (field, value) => {
    setCompliance(prev => ({ ...prev, [field]: value }));
  };

  const handleCountryChange = (e) => {
    const code = e.target.value;
    const found = COUNTRIES.find(c => c.code === code);
    setFormData(prev => ({
      ...prev,
      country_code: code,
      country: found ? found.name : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        ...formData,
        advanced_compute_enquiry: isAdvanced,
        compliance: isAdvanced ? compliance : null,
        requested_category: requestedCategory || null,
        requested_type: requestedType || requestedSub || null,
      });
      setStatus('success');
      setFormData({
        name: '', email: '', company: '', phone: '', address: '',
        country: '', country_code: '', postcode: '', vat_number: '', subject: '', message: '', consent_offers: false
      });
      setCompliance(EMPTY_COMPLIANCE);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Failed to send message. Please try again.');
    }
  };

  return (
    <main className="pt-32 pb-20" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-[#0096D6]/10 text-[#0096D6] font-manrope text-sm font-medium mb-4">
            Get in Touch
          </span>
          <h1 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Contact Us
          </h1>
          <p className="font-manrope text-slate-600 text-lg max-w-2xl mx-auto">
            Ready to discuss your wholesale electronics needs? Our team is here to help you find the perfect solutions for your business.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Form — First */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-2"
            id="enquiry-form"
          >
            <div className="opulent-card p-8 md:p-10">
              <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-6">
                Send Us a Message
              </h2>

              {status === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                  data-testid="contact-success-message"
                >
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">
                    Message Sent Successfully!
                  </h3>
                  <p className="font-manrope text-slate-600 mb-6">
                    Thank you for reaching out. Our team will get back to you within 24-48 hours.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="btn-secondary"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {requestedLabel && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[#0096D6]/5 border border-[#0096D6]/15" data-testid="contact-requested-category">
                      <Package className="w-4 h-4 text-[#0096D6] shrink-0" />
                      <span className="font-manrope text-sm text-slate-700">Enquiring about: <strong>{requestedLabel}</strong></span>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:border-[#0096D6] font-manrope"
                        placeholder="John Smith"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:border-[#0096D6] font-manrope"
                        placeholder="john@company.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:border-[#0096D6] font-manrope"
                        placeholder="Your Company Ltd"
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div>
                      <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:border-[#0096D6] font-manrope"
                        placeholder="+44 123 456 7890"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                        Company Address
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:border-[#0096D6] font-manrope"
                        placeholder="123 Business Street, City"
                        data-testid="contact-address-input"
                      />
                    </div>
                    <div>
                      <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                        Country *
                      </label>
                      <select
                        name="country_code"
                        value={formData.country_code}
                        onChange={handleCountryChange}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:border-[#0096D6] font-manrope"
                        data-testid="contact-country-select"
                      >
                        <option value="">Select your country</option>
                        {COUNTRIES.map(c =>
                          c.disabled ? (
                            <option key={c.code} disabled>─────────────</option>
                          ) : (
                            <option key={c.code} value={c.code}>{c.name}</option>
                          )
                        )}
                      </select>
                    </div>
                  </div>

                  {formData.country_code === 'GB' && (
                    <div>
                      <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                        UK Postcode
                      </label>
                      <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:border-[#0096D6] font-manrope"
                        placeholder="E14 5NR"
                        data-testid="contact-postcode-input"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                      VAT Number
                    </label>
                    <input
                      type="text"
                      name="vat_number"
                      value={formData.vat_number}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:border-[#0096D6] font-manrope"
                      placeholder="GB 123 456 789"
                      data-testid="contact-vat-input"
                    />
                    <p className="font-manrope text-xs text-slate-400 mt-1.5">
                      Please provide your VAT number where applicable. VAT details may be required for compliance with tax and government regulations in relevant jurisdictions.
                    </p>
                  </div>

                  <div>
                    <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                      Subject *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:border-[#0096D6] font-manrope"
                      data-testid="contact-subject-select"
                    >
                      <option value="">Select a subject</option>
                      <option value={ADVANCED_SUBJECT}>{ADVANCED_SUBJECT}</option>
                      <option value="Wholesale Inquiry">Wholesale Inquiry</option>
                      <option value="Product Information">Product Information</option>
                      <option value="Partnership Opportunity">Partnership Opportunity</option>
                      <option value="Bulk Order Quote">Bulk Order Quote</option>
                      <option value="General Inquiry">General Inquiry</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:border-[#0096D6] font-manrope resize-none"
                      placeholder="Tell us about your requirements..."
                      data-testid="contact-message-textarea"
                    />
                  </div>

                  {isAdvanced && <ComplianceFieldset values={compliance} onChange={handleComplianceChange} />}

                  {status === 'error' && (
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-xl" data-testid="contact-error-message">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-manrope text-sm">{errorMessage}</span>
                    </div>
                  )}

                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <input
                      type="checkbox"
                      name="consent_offers"
                      checked={formData.consent_offers}
                      onChange={handleChange}
                      className="w-4 h-4 mt-0.5 rounded border-slate-300 text-[#0096D6]"
                      id="consent_offers"
                      data-testid="contact-consent-checkbox"
                    />
                    <label htmlFor="consent_offers" className="font-manrope text-sm text-slate-600">
                      I consent to receiving product offers and wholesale opportunities from Real Tech Services Ltd. You can unsubscribe at any time.
                    </label>
                  </div>

                  <motion.button
                    type="submit"
                    disabled={status === 'loading'}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-primary w-full md:w-auto flex items-center justify-center gap-2 disabled:opacity-70"
                    data-testid="contact-submit-button"
                  >
                    {status === 'loading' ? (
                      <>Sending...</>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>

          {/* Contact Info — After form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-1 space-y-6"
          >
            {contactInfo.map((item, index) => (
              <div
                key={index}
                className="opulent-card p-6 flex items-start gap-4"
                data-testid={`contact-info-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#0096D6]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-[#0096D6]" />
                </div>
                <div>
                  <h3 className="font-outfit font-semibold text-slate-900 mb-1">
                    {item.title}
                  </h3>
                  <p className="font-manrope text-slate-700">{item.content}</p>
                  <p className="font-manrope text-slate-500 text-sm">{item.subtext}</p>
                </div>
              </div>
            ))}

            <div className="opulent-card overflow-hidden h-48">
              <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-8 h-8 text-[#0096D6] mx-auto mb-2" />
                  <p className="font-manrope text-slate-500 text-sm">London, United Kingdom</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </main>
  );
}
